import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRight, Users } from 'lucide-react';
import { api } from '@/lib/api';

interface AgenteWithKpis {
  id: number;
  email: string;
  nombre: string;
  avatarUrl: string | null;
  activo: boolean;
  kpis: {
    bookings: {
      sin_iniciar: number;
      en_proceso: number;
      completado: number;
      detenido: number;
    };
    itemsLeaf: {
      sin_iniciar: number;
      en_proceso: number;
      completado: number;
      detenido: number;
    };
    itemsVencidos: number;
    itemsPendientes: number;
  };
}

export function EquipoPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['team-agentes'],
    queryFn: () => api<{ items: AgenteWithKpis[] }>('/team/agentes'),
  });

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-6xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">Equipo</h1>
        <p className="text-sm text-ink-400 mt-1">
          {isLoading
            ? '—'
            : `${data?.items.length ?? 0} agente${data?.items.length === 1 ? '' : 's'} bajo tu supervisión`}
        </p>
      </div>

      {isLoading && <div className="text-center py-12 text-ink-400">Cargando…</div>}

      {!isLoading && data?.items.length === 0 && (
        <div className="rounded-lg border border-dashed border-ink-200 p-12 text-center text-ink-400">
          <Users className="mx-auto h-8 w-8 mb-2 opacity-50" />
          <p>No tienes agentes asignados todavía.</p>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {data?.items.map((a) => (
          <Link
            key={a.id}
            to={`/board/${a.id}`}
            className="rounded-lg border border-ink-200 bg-surface-1 p-4 hover:border-brand-300 hover:shadow-sm transition group"
          >
            <div className="flex items-center gap-3">
              {a.avatarUrl ? (
                <img
                  src={a.avatarUrl}
                  alt={a.nombre}
                  className="w-10 h-10 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-brand-500 text-white text-sm font-medium flex items-center justify-center">
                  {a.nombre
                    .split(' ')
                    .map((s) => s[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{a.nombre}</div>
                <div className="text-xs text-ink-400 truncate">{a.email}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-ink-300 group-hover:text-brand-500" />
            </div>

            <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
              <Kpi
                label="Activos"
                value={a.kpis.bookings.en_proceso + a.kpis.bookings.sin_iniciar}
                color="ink"
              />
              <Kpi label="Pendientes" value={a.kpis.itemsPendientes} color="amber" />
              <Kpi label="Vencidas" value={a.kpis.itemsVencidos} color="brand" />
              <Kpi
                label="Completadas"
                value={a.kpis.bookings.completado}
                color="green"
              />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: 'ink' | 'amber' | 'brand' | 'green';
}) {
  const colorClass = {
    ink: 'text-ink-900',
    amber: value > 0 ? 'text-amber-600' : 'text-ink-300',
    brand: value > 0 ? 'text-brand-500' : 'text-ink-300',
    green: 'text-emerald-600',
  }[color];
  return (
    <div className="rounded-md bg-surface-2 py-1.5">
      <div className={`text-base font-semibold tabular ${colorClass}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-ink-400">{label}</div>
    </div>
  );
}
